import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { API, api, errMsg, fmtDate, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Badge } from "@/components/Shared";
import { PayFeeButton } from "@/components/PayFeeButton";
import { PlanComparison } from "@/components/PlanComparison";
import { TaxInvoices } from "@/components/TaxInvoices";

export default function Fees() {
  const { user } = useAuth();
  const isClient = user?.role === "client";
  const isAdvocate = user?.role === "advocate";
  const isArbitrator = user?.role === "arbitrator";
  const [rows, setRows] = useState([]);
  const [payEnabled, setPayEnabled] = useState(false);

  const load = () => api.get("/invoices").then(({ data }) => setRows(data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
    api.get("/payments/status").then(({ data }) => setPayEnabled(data.razorpay_enabled)).catch(() => {});
  }, []);

  const markPaid = async (id) => {
    try {
      await api.put(`/invoices/${id}`, { status: "paid" });
      toast.success("Fee note recorded as settled");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const unpaid = rows.filter((r) => r.status === "unpaid").reduce((s, r) => s + (r.amount || 0), 0);
  const paid = rows.filter((r) => r.status === "paid").reduce((s, r) => s + (r.amount || 0), 0);

  return (
    <div data-testid="fees-page">
      {!isArbitrator && <div className="mb-8"><TaxInvoices /></div>}
      <PageHeader
        eyebrow="Fees"
        title={isClient ? "Fees Payable" : isArbitrator ? "My Fee Notes" : "Fees & Subscriptions"}
        description={
          isClient
            ? "Every case fee is shared equally between the claimant's side and the respondent's side. Pay your half securely by UPI, card or netbanking through Razorpay — your case record opens as soon as it is received."
            : isArbitrator
            ? "Your fee notes in the references before you, and the receipts once they are settled. The registry's package fee between the parties is not shown here."
            : "Case fees are raised in two halves, one for each side. Your side's half may be paid from here and opens your side's record at once, including hearing dates posted by the tribunal."
        }
      />

      {(isClient || isAdvocate) && (
        <div className="mb-8">
          <PlanComparison />
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8 stagger">
        <div className="border border-border bg-card p-6" data-testid="fees-outstanding">
          <p className="label-caps">Outstanding</p><p className="font-display text-3xl mt-3">{money(unpaid)}</p>
        </div>
        <div className="border border-border bg-card p-6" data-testid="fees-collected">
          <p className="label-caps">Settled</p><p className="font-display text-3xl mt-3">{money(paid)}</p>
        </div>
        <div className="border border-border bg-card p-6" data-testid="fees-count">
          <p className="label-caps">Fee notes</p><p className="font-display text-3xl mt-3">{rows.length}</p>
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState testId="fees-empty" title="No fee notes raised"
          hint={isArbitrator ? "Raise your fee note from the case file." : "The arbitrator raises fee notes from the case file."} />
      ) : (
        <div className="border border-border bg-card overflow-x-auto" data-testid="invoices-table">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="border-b border-border bg-muted/60">
                {["Invoice", "Head", "Case", "Payable by", "Particulars", "Amount", "Due", "Status", ""].map((h) => (
                  <th key={h} className="p-4 text-left label-caps font-normal">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((i) => (
                <tr key={i.id} className="border-b border-border last:border-0 hover:bg-accent transition-colors duration-200" data-testid={`invoice-list-row-${i.id}`}>
                  <td className="p-4 font-mono text-xs">{i.invoice_number}</td>
                  <td className="p-4">
                    <Badge className={
                      i.kind === "subscription" ? "bg-indigo-50 text-indigo-900 dark:bg-indigo-950 dark:text-indigo-200"
                      : i.kind === "subscription_upgrade" ? "bg-amber-50 text-amber-900 dark:bg-amber-950 dark:text-amber-200"
                      : "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200"}>
                      {i.kind === "subscription"
                        ? `case fee · ${i.side === "respondent" ? "respondent" : "claimant"} half`
                        : i.kind === "subscription_upgrade"
                        ? `upgrade · ${i.side === "respondent" ? "respondent" : "claimant"} half`
                        : "arbitrator's fee"}
                    </Badge>
                  </td>
                  <td className="p-4">
                    <Link to={`/cases/${i.case_id}`} className="font-mono text-xs text-blue-900 dark:text-blue-400 hover:underline">{i.ref_id}</Link>
                  </td>
                  <td className="p-4">{i.payable_to}</td>
                  <td className="p-4 text-muted-foreground">{i.particulars || "—"}</td>
                  <td className="p-4 font-mono">{money(i.amount, i.currency)}</td>
                  <td className="p-4 text-muted-foreground whitespace-nowrap">{fmtDate(i.due_on)}</td>
                  <td className="p-4"><Badge className={i.status === "paid" ? "bg-green-50 text-green-900" : "bg-red-50 text-red-900"}>{i.status}</Badge></td>
                  <td className="p-4">
                    {i.status === "paid" ? (
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground whitespace-nowrap">Paid {fmtDate(i.paid_at)}</span>
                        <a href={`${API}/invoices/${i.id}/receipt`} target="_blank" rel="noreferrer"
                          data-testid={`receipt-${i.id}`}
                          className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-blue-900 dark:text-blue-400 hover:opacity-70 transition-opacity duration-200">
                          <Download className="h-3.5 w-3.5" /> Receipt
                        </a>
                      </div>
                    ) : isClient || (isAdvocate && i.kind !== "arbitrator_fee") ? (
                      <PayFeeButton invoice={i} enabled={payEnabled} onPaid={load} />
                    ) : isArbitrator ? (
                      <button data-testid={`invoice-mark-paid-${i.id}`}
                        onClick={() => window.confirm(`Confirm that ${i.invoice_number} has been received offline and verified?`) && markPaid(i.id)}
                        className="text-xs uppercase tracking-wider text-blue-900 dark:text-blue-400 hover:opacity-70 transition-opacity duration-200">
                        Mark paid (verified)
                      </button>
                    ) : (
                      <span className="text-xs text-muted-foreground" data-testid={`invoice-awaiting-${i.id}`}>Awaiting payment</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isClient && !payEnabled && (
        <p className="mt-6 text-xs text-muted-foreground" data-testid="payment-disabled-note">
          Online payment goes live as soon as the Razorpay Key ID and Key Secret are configured.
        </p>
      )}
    </div>
  );
}
